import { Link, useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import PasswordInput from '../common/PasswordInput';
import { useState } from 'react';
import axios from 'axios';
import CircularIndeterminate from '../common/Progress';
import PrimaryButton from '../common/button/PrimaryButton';
import { Api } from '../../redux-state/docs/api';

const SERVER_URL = process.env.REACT_APP_API_URL;

const SignupForm = () => {
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setEmailError('');

    if (!validateEmail(email)) {
      setLoading(false);
      setEmailError('Please enter a valid email address.');
      return;
    }

    const passwordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordPattern.test(password)) {
      setLoading(false);
      setError('Password must be 8+ characters with uppercase, lowercase, number, and special character.');
      return;
    }

    const formData = new FormData();
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    formData.append('email', email);
    formData.append('password', password);

    try {
      const csrfToken = await Api.getCsrfToken();
      const response = await axios.post(`${SERVER_URL}/api/signupUserV2`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': csrfToken,
        },
        withCredentials: true,
      });

      const data = response.data;

      if (data.status === 200) {
        localStorage.setItem('email', email);
        navigate('/verifyUser');
      } else {
        setError(data.message || 'An unknown error occurred.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      const errorMessage = err.response?.data?.message || 'An error occurred during signup. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="h-[100%] flex flex-row items-center justify-center">
        <form className="bg-white rounded px-4 lg:px-8  pt-6 pb-8 mb-4 w-full max-w-[600px]" onSubmit={handleSubmit}>
          <div className="text-2xl md:text-3xl font-bold text-center">Sign up for an account</div>
          <p className="text-center text-gray-500 mt-2 mb-6">Let's get started</p>

          <div className="flex gap-4 mb-4">
            <TextField
              required
              className="w-1/2"
              id="firstName"
              label="First Name"
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              InputProps={{ className: 'boarder-radius-10' }}
            />
            <TextField
              required
              className="w-1/2"
              id="lastName"
              label="Last Name"
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              InputProps={{ className: 'boarder-radius-10' }}
            />
          </div>

          <div className="mb-4">
            <div className="mb-4 mt-6">
              <TextField
                required
                className="w-full"
                id="email"
                label="Enter Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                InputProps={{ className: 'boarder-radius-10' }}
              />
            </div>
          </div>

          <div className="mb-4">
            <PasswordInput
              className="w-full"
              password={password}
              handlePassword={(e) => setPassword(e.target.value)}
              error={!!error && error.toLowerCase().includes("password")}
              helperText={error && error.toLowerCase().includes("password") ? error : ''}
            />
          </div>

          <p className="text-xs text-gray-500 mt-1 mb-6">
            Password must be 8+ characters with one uppercase, lowercase, number, and special character.
          </p>

          <PrimaryButton
            text={loading ? 'Signing Up...' : 'Sign Up'}
            type="submit"
            className="w-full"
            disabled={loading}
          />

          {error && !error.toLowerCase().includes("password") && (
            <p className="text-red-500 text-sm mt-2">{error}</p>
          )}

          <p className="text-center text-gray-700 text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="font-bold cursor-pointer">Login</Link>
          </p>
        </form>
      </div>

      <div className="justify-self-end flex flex-row w-[100%] justify-between bottom-20 p-8">
        <div>Privacy policy</div>
        <div>Copyright 2025</div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <CircularIndeterminate />
        </div>
      )}
    </>
  );
};

export default SignupForm;
